/**
 * ESN-Style Accordion Variant Picker
 * variant-accordion.js
 *
 * VariantAccordion extends VariantSelects (defined in global.js) so it
 * inherits ALL price / media / URL / add-to-cart update logic automatically.
 *
 * When a user clicks an item row we:
 *   1. Update the hidden <select> value
 *   2. Fire 'change' on <variant-accordion> itself → onVariantChange() runs
 *   3. onVariantChange() → updateOptions() reads hidden selects → finds variant
 *   4. Price, media, URL, inventory, add-button all update via inherited methods
 */

(function () {
  customElements.whenDefined('variant-selects').then(() => {
    const VariantSelectsClass = customElements.get('variant-selects');

class VariantAccordion extends VariantSelectsClass {
  constructor() {
    super();
    // Remove the generic listener added by VariantSelects constructor
    // to avoid double-firing on bubbled select change events.
    this.removeEventListener('change', this.onVariantChange);
    this._sections = {};
    this._outsideClickHandler = null;
  }

  connectedCallback() {
    // Re-attach the change listener on THIS element (not on child selects)
    this.addEventListener('change', this.onVariantChange.bind(this));

    // Cache DOM sections per option index
    this.querySelectorAll('.vac-group').forEach((group) => {
      const optIndex     = parseInt(group.dataset.optionIndex, 10);
      const header       = group.querySelector('.vac-header');
      const panel        = group.querySelector('.vac-panel');
      const hiddenSelect = group.querySelector('.vac-hidden-select');
      const selectedLabel = this.querySelector(`[data-vac-selected="${optIndex}"]`);
      const headerThumb   = group.querySelector('.vac-header__thumb');

      this._sections[optIndex] = { group, header, panel, hiddenSelect, selectedLabel, headerThumb };

      // Toggle open/close
      header.addEventListener('click', () => this._togglePanel(optIndex));

      // Item row click + keyboard
      group.querySelectorAll('.vac-item').forEach((item) => {
        item.setAttribute('tabindex', '0');
        item.addEventListener('click', () => {
          if (item.classList.contains('is-disabled')) return;
          this._selectValue(optIndex, item.dataset.value);
        });
        item.addEventListener('keydown', (e) => {
          if (e.key === 'Enter' || e.key === ' ') {
            e.preventDefault();
            if (!item.classList.contains('is-disabled')) this._selectValue(optIndex, item.dataset.value);
          }
        });
      });
    });

    this._outsideClickHandler = (e) => { if (!this.contains(e.target)) this._closeAll(); };
    document.addEventListener('click', this._outsideClickHandler);
  }

  disconnectedCallback() {
    if (this._outsideClickHandler) document.removeEventListener('click', this._outsideClickHandler);
  }

  // ── Override: read values from our hidden selects ──────────────────
  updateOptions() {
    this.options = Array.from(
      this.querySelectorAll('.vac-hidden-select'),
      (select) => select.value
    );
  }

  // ── Override: skip native :checked radio logic — we handle availability ──
  updateVariantStatuses() {
    this._updateAvailability();
  }

  // ── Toggle a panel ──────────────────────────────────────────────────
  _togglePanel(optIndex) {
    const sec = this._sections[optIndex];
    if (!sec) return;
    const isOpen = sec.group.classList.contains('is-open');
    this._closeAll();
    if (!isOpen) {
      sec.group.classList.add('is-open');
      sec.header.setAttribute('aria-expanded', 'true');
      sec.panel.removeAttribute('hidden');
      if (window.innerWidth < 750) {
        setTimeout(() => sec.group.scrollIntoView({ behavior: 'smooth', block: 'nearest' }), 50);
      }
    }
  }

  // ── Close all panels ────────────────────────────────────────────────
  _closeAll() {
    Object.values(this._sections).forEach(({ group, header, panel }) => {
      group.classList.remove('is-open');
      header.setAttribute('aria-expanded', 'false');
      panel.setAttribute('hidden', '');
    });
  }

  // ── User selects a value row ────────────────────────────────────────
  _selectValue(optIndex, value) {
    const sec = this._sections[optIndex];
    if (!sec) return;

    // 1. Update hidden select
    sec.hiddenSelect.value = value;

    // 2. Update item UI
    sec.group.querySelectorAll('.vac-item').forEach((item) => {
      const sel = item.dataset.value === value;
      item.classList.toggle('is-selected', sel);
      item.setAttribute('aria-selected', String(sel));
    });

    // 3. Update header
    this._updateHeader(optIndex, value);

    // 4. Close panel
    this._closeAll();

    // 5. Fire 'change' on THIS element → inherited onVariantChange() handles
    //    price, media, URL, add-button, inventory etc.
    this.dispatchEvent(new Event('change', { bubbles: true }));
  }

  // ── Update collapsed header label + thumbnail ───────────────────────
  _updateHeader(optIndex, value) {
    const sec = this._sections[optIndex];
    if (!sec) return;
    if (sec.selectedLabel) sec.selectedLabel.textContent = value;

    const selectedItem = sec.group.querySelector(`.vac-item[data-value="${CSS.escape(value)}"]`);
    if (selectedItem && sec.headerThumb) {
      const itemThumb = selectedItem.querySelector('.vac-item__thumb');
      if (itemThumb) {
        sec.headerThumb.style.backgroundImage    = itemThumb.style.backgroundImage || '';
        sec.headerThumb.style.backgroundColor    = itemThumb.style.backgroundColor || '#f0f0f0';
        sec.headerThumb.style.backgroundSize     = itemThumb.style.backgroundSize || 'cover';
        sec.headerThumb.style.backgroundRepeat   = 'no-repeat';
        sec.headerThumb.style.backgroundPosition = 'center';
        sec.headerThumb.classList.toggle(
          'vac-header__thumb--no-img',
          !itemThumb.style.backgroundImage && !itemThumb.style.backgroundColor
        );
      }
    }
  }

  // ── Re-evaluate availability ────────────────────────────────────────
  _updateAvailability() {
    const variants = this.getVariantData();
    const sel = {};
    Object.entries(this._sections).forEach(([idx, sec]) => {
      sel[parseInt(idx, 10)] = sec.hiddenSelect.value;
    });

    Object.entries(this._sections).forEach(([idxStr, sec]) => {
      const optIndex = parseInt(idxStr, 10);
      sec.group.querySelectorAll('.vac-item').forEach((item) => {
        const testVal = item.dataset.value;
        let available = false;
        for (const v of variants) {
          let matches = true;
          for (let i = 1; i <= 3; i++) {
            if (v[`option${i}`] === undefined) continue;
            if (i === optIndex) {
              if (v[`option${i}`] !== testVal) { matches = false; break; }
            } else if (sel[i]) {
              if (v[`option${i}`] !== sel[i]) { matches = false; break; }
            }
          }
          if (matches && v.available) { available = true; break; }
        }
        item.classList.toggle('is-disabled', !available);
      });
    });
  }
}

    if (!customElements.get('variant-accordion')) {
      customElements.define('variant-accordion', VariantAccordion);
    }
  }); // end whenDefined
})(); // end IIFE
